import { X } from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";

const CATEGORIES = [
  { key: "financial", label: "Financial Resources", short: "Financial", weight: 35 },
  { key: "impact", label: "Impact & Innovation", short: "Impact", weight: 30 },
  { key: "prestige", label: "Prestige & Value", short: "Prestige", weight: 20 },
  { key: "sustainability", label: "Sustainability", short: "Sustain", weight: 10 },
  { key: "brand", label: "Brand Awareness", short: "Brand", weight: 5 },
];

function Slot({ inst, onClear, index }) {
  const fallback = index === 0 ? "#B8912F" : "#0F172A";
  if (!inst) {
    return (
      <div className="cmp-slot cmp-slot-empty" data-testid={`compare-slot-${index}`}>
        <span className="cmp-slot-badge">{index === 0 ? "A" : "B"}</span>
        <span className="cmp-slot-hint">Pick an institution</span>
      </div>
    );
  }
  const color = inst.color || fallback;
  return (
    <div className="cmp-slot" data-testid={`compare-slot-${index}`}>
      <span className="cmp-slot-badge" style={{ background: color }}>
        {index === 0 ? "A" : "B"}
      </span>
      <div className="cmp-slot-info">
        <span className="cmp-slot-name">{inst.short}</span>
        <span className="cmp-slot-score" style={{ color }}>
          {inst.resiliency_rating.toFixed(2)}
        </span>
      </div>
      <button
        className="cmp-slot-clear"
        data-testid={`compare-clear-${index}`}
        onClick={() => onClear(inst.rank)}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export function ComparePanel({ a, b, onClear }) {
  const aColor = a?.color || "#B8912F";
  const bColor = b?.color || "#0F172A";

  const radarData = CATEGORIES.map((c) => ({
    cat: c.short,
    A: a ? a[c.key] ?? 0 : 0,
    B: b ? b[c.key] ?? 0 : 0,
  }));

  return (
    <section className="hud-dashboard hud-compare" data-testid="compare-panel">
      <div className="dash-head">
        <span className="hud-eyebrow">Compare Mode</span>
        <h2 className="dash-name">Head-to-Head</h2>
        <p className="dash-meta cmp-meta">Select up to two institutions</p>
      </div>

      <div className="cmp-slots">
        <Slot inst={a} onClear={onClear} index={0} />
        <Slot inst={b} onClear={onClear} index={1} />
      </div>

      {a && b && (
        <div className="cmp-radar" data-testid="compare-radar">
          <ResponsiveContainer width="100%" height={210}>
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="rgba(15,23,42,0.12)" />
              <PolarAngleAxis
                dataKey="cat"
                tick={{ fill: "#475569", fontSize: 10, fontWeight: 600 }}
              />
              <Radar
                dataKey="A"
                stroke={aColor}
                fill={aColor}
                fillOpacity={0.28}
                strokeWidth={2}
                isAnimationActive={false}
              />
              <Radar
                dataKey="B"
                stroke={bColor}
                fill={bColor}
                fillOpacity={0.24}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="dash-categories cmp-categories">
        {CATEGORIES.map((c) => {
          const av = a?.[c.key];
          const bv = b?.[c.key];
          return (
            <div className="cmp-cat" key={c.key} data-testid={`compare-cat-${c.key}`}>
              <div className="cmp-cat-label">
                {c.label}
                <em className="dash-cat-weight">{c.weight}%</em>
              </div>
              <div className="cmp-row">
                <span className="cmp-val" style={{ color: aColor, textAlign: "right" }}>
                  {av == null ? (a ? "N/A" : "—") : av.toFixed(1)}
                </span>
                <div className="cmp-track">
                  <div
                    className="cmp-fill"
                    style={{ width: `${av == null ? 0 : av}%`, background: aColor }}
                  />
                  <div
                    className="cmp-fill"
                    style={{ width: `${bv == null ? 0 : bv}%`, background: bColor }}
                  />
                </div>
                <span className="cmp-val" style={{ color: bColor, textAlign: "left" }}>
                  {bv == null ? (b ? "N/A" : "—") : bv.toFixed(1)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
